import styles from "./Home.module.css";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useHomeData } from "../hooks/useHomeData";

import manImg from "../../../assets/icons/man_img.svg";
import womanImg from "../../../assets/icons/woman_img.svg";
import exclamationIcon from "../../../assets/icons/ExclamationCircle.svg";
import editIcon from "../../../assets/icons/edit_icon.svg";
import { WhiteButton } from "@react-solutions-ui/findmore-ui";
import EvaluateTeamList from "../components/EvaluationDashboard/EvaluateTeamList";
import EvaluateColleaguesList from "../components/EvaluationDashboard/EvaluateColleaguesList";
import { BlueButton } from "@react-solutions-ui/findmore-ui";
import { StatusBadge } from "@react-solutions-ui/findmore-ui";
import { ReactComponent as ArrowIcon } from "../../../assets/icons/arrowIcon.svg";
import InfoIcon from "../../../assets/icons/infoIcon.svg";
import { SuccessToast } from "@react-solutions-ui/findmore-ui";
import EvaluationInfoModal from "../../../components/ui/EvaluationInfo/EvaluationInfoModal";
import CloseIcon from "../../../assets/icons/closeIconBlack.svg";
import { getTimeUntil, TimeRemaining } from "../../../utils/dateUtil";

export default function Home() {
  const navigate = useNavigate();
  const { user, homeDashboard, loadingDashboard } = useHomeData();
  const [showEvaluators, setShowEvaluators] = useState(true);
  const [globalToast, setGlobalToast] = useState("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [hideWarning, setHideWarning] = useState(false);
  const [hiddenEvaluatorWarnings, setHiddenEvaluatorWarnings] = useState<
    Record<string, boolean>
  >({});

  const activeEvaluation = homeDashboard?.activeEvaluation ?? null;
  const hasActiveEvaluation =
    activeEvaluation && activeEvaluation.statusReasonLabel !== "Completed";

  const handleCloseModal = () => {
    setIsInfoModalOpen(false);
  };

  const handleOpenModal = () => {
    setIsInfoModalOpen(true);
  };

  const readyToRender = !!user && !loadingDashboard;
  const colleagueEvaluatorsCount =
    activeEvaluation?.evaluators?.filter(
      (e) =>
        e.evaluatorTypeChoice === "Colleague" ||
        e.evaluatorTypeChoice === "Friend"
    ).length ?? 0;

  const teamLeaderOwnEvaluations =
    homeDashboard?.teamLeaderOwnEvaluations ?? [];
  const teamLeadersList = teamLeaderOwnEvaluations.filter(
    (e) => e.employeeTeamTypeChoice === "Team Leader"
  );
  const internalsList = teamLeaderOwnEvaluations.filter(
    (e) => e.employeeTeamTypeChoice === "Internal"
  );

  const deadlineAlreadyPassed =
    new Date(activeEvaluation?.deadlineToChooseEvaluators ?? "") < new Date();
  const selfEvaluationSubmissionDate = activeEvaluation?.modifiedOn;

  return (
    <div className={`${styles.homeContainer} page-container`}>
      {readyToRender ? (
        <>
          {globalToast && (
            <SuccessToast
              message={globalToast}
              onClose={() => setGlobalToast("")}
            />
          )}
          <EvaluationInfoModal
            isOpen={isInfoModalOpen}
            onClose={handleCloseModal}
          />
          <header className={styles.homeHeader}>
            <div className={styles.headerText}>
              <h1 className={styles.headerTitle}>Avaliação 360 do consultor</h1>
              <p className={styles.headerSubtitle}>
                Bem-vindo, {user?.name} · {user?.employeeAreaName} ·{" "}
                {user?.typeLabel}
              </p>
            </div>
          </header>

          {hasActiveEvaluation ? (
            <div className={styles.evaluationBox}>
              {/* Conteúdo da avaliação */}
              <h2 className={styles.evaluationTitle}>
                Avaliação {activeEvaluation.year}
                <button
                  type="button"
                  className={styles.infoButton}
                  onClick={handleOpenModal}
                  aria-label="Como funciona a avaliação"
                >
                  <img src={InfoIcon} alt="" className={styles.infoIcon} />
                </button>
              </h2>
              <div className={styles.evaluationStatusBox}>
                <div className={styles.evaluationText}>
                  {activeEvaluation?.status === "Concluído" ? (
                    <>
                      Autoavaliação submetida em{" "}
                      {new Date(
                        selfEvaluationSubmissionDate ?? activeEvaluation.endDate
                      ).toLocaleDateString("pt-PT")}
                    </>
                  ) : (
                    <>
                      Tens até{" "}
                      {activeEvaluation &&
                        new Date(activeEvaluation.endDate).toLocaleDateString(
                          "pt-PT"
                        )}{" "}
                      para completar a tua autoavaliação.
                    </>
                  )}
                </div>
                <div className={styles.evaluationTimeBox}>
                  <span className={styles.evaluationTime}>Tempo restante:</span>{" "}
                  <span className={styles.evaluationTime}>
                    {(() => {
                      if (!activeEvaluation?.endDate) return "-";
                      const now = new Date();
                      const end = new Date(activeEvaluation.endDate);
                      const diffDays = Math.ceil(
                        (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                      );
                      return diffDays > 0 ? `${diffDays} dias` : "Terminou";
                    })()}
                  </span>
                </div>
              </div>

              <div
                className={`${styles.evaluationBoxBottons} ${
                  showEvaluators ? styles.evaluationBoxBottonsWithMargin : ""
                }`}
              >
                <div className={styles.evaluationButtons}>
                  {colleagueEvaluatorsCount < 2 &&
                    activeEvaluation?.deadlineToChooseEvaluators &&
                    !deadlineAlreadyPassed && (
                      <WhiteButton
                        text="SELECIONAR COLEGAS"
                        onClick={() => navigate("/select-colleagues")}
                        className={styles.evaluationBtn}
                      />
                    )}
                  <BlueButton
                    text={
                      activeEvaluation?.status === "Concluído"
                        ? "VER AUTOAVALIAÇÃO"
                        : "INICIAR AUTOAVALIAÇÃO"
                    }
                    onClick={() => {
                      navigate(
                        `/questionnaire?id=${activeEvaluation?.selfEvaluatorId}`
                      );
                    }}
                    className={styles.evaluationBtn}
                  />
                </div>

                <button
                  type="button"
                  className={styles.evaluationProcess}
                  onClick={() => setShowEvaluators(!showEvaluators)}
                  aria-expanded={showEvaluators}
                >
                  <span>O MEU PROCESSO DE AVALIAÇÃO</span>
                  <span
                    className={`${styles.arrow} ${
                      showEvaluators ? styles.arrowUp : styles.arrowDown
                    }`}
                  >
                    <ArrowIcon className={styles.arrowIcon} />
                  </span>
                </button>
              </div>

              {/* Dias para escolher colegas */}
              {(() => {
                const now = new Date();
                const deadline = new Date(
                  activeEvaluation.deadlineToChooseEvaluators
                );
                const diffDaysToChoose = Math.floor(
                  (deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                );

                const showWarning =
                  colleagueEvaluatorsCount < 2 && diffDaysToChoose > 0;

                if (showWarning && !hideWarning) {
                  return (
                    <div
                      className={`${styles.warningBox} ${
                        showEvaluators ? styles.warningWithProcessOpen : ""
                      }`}
                    >
                      <div className={styles.warningContent}>
                        <img
                          src={exclamationIcon}
                          alt="Warning Icon"
                          className={styles.warningIcon}
                        />
                        <span className={styles.warningText}>
                          Tens apenas {diffDaysToChoose} dias para selecionar os
                          colegas
                        </span>
                      </div>
                      <button
                        className={styles.warningClose}
                        onClick={() => setHideWarning(true)}
                      >
                        <img src={CloseIcon} alt="Fechar" />
                      </button>
                    </div>
                  );
                }
              })()}

              {showEvaluators &&
                (activeEvaluation?.evaluators?.length ?? 0) > 0 && (
                  <ul className={styles.evaluatorsList}>
                    {(() => {
                      const typeOrder: { [key: string]: number } = {
                        Management: 1,
                        "Team Leader": 2,
                        Client: 3,
                        Colleague: 4,
                        Friend: 5,
                      };

                      const sortedEvaluators = [
                        ...activeEvaluation?.evaluators,
                      ].sort((a, b) => {
                        const orderA = typeOrder[a.evaluatorTypeChoice] || 999;
                        const orderB = typeOrder[b.evaluatorTypeChoice] || 999;
                        return orderA - orderB;
                      });

                      return sortedEvaluators.map((evaluator, idx) => (
                        <li
                          key={`${evaluator.exployeeId}-${idx}`}
                          className={styles.evaluatorWrapper}
                        >
                          <div className={styles.evaluatorItemRow}>
                            <div className={styles.evaluatorInfo}>
                              <span className={styles.evaluatorChoice}>
                                {(() => {
                                  if (
                                    evaluator.evaluatorTypeChoice === "Client"
                                  ) {
                                    return "Cliente";
                                  } else if (
                                    evaluator.evaluatorTypeChoice ===
                                    "Colleague"
                                  ) {
                                    return "Colega técnico";
                                  } else if (
                                    evaluator.evaluatorTypeChoice === "Friend"
                                  ) {
                                    return "Colega social";
                                  } else {
                                    return evaluator.evaluatorTypeChoice;
                                  }
                                })()}
                              </span>
                              <span className={styles.evaluatorName}>
                                {evaluator.evaluatorTypeChoice === "Client"
                                  ? evaluator.clientName ?? ""
                                  : evaluator.evaluatorEmployeeName}
                              </span>
                              <span>
                                {evaluator?.evaluatedEmployeeAreaName}
                              </span>
                            </div>
                            <div className={styles.evaluatorStatusBox}>
                              <StatusBadge status={evaluator.status} />
                              {activeEvaluation.deadlineToChooseEvaluators >
                                new Date().toISOString() &&
                                evaluator.status === "Pendente" && (
                                  <button
                                    className={styles.editColleagueLink}
                                    onClick={() => {
                                      if (
                                        evaluator.evaluatorTypeChoice ===
                                        "Colleague"
                                      ) {
                                        navigate(
                                          "/select-colleagues?editingWork=true"
                                        );
                                      } else if (
                                        evaluator.evaluatorTypeChoice ===
                                        "Friend"
                                      ) {
                                        navigate(
                                          "/select-colleagues?editingSocial=true"
                                        );
                                      }
                                    }}
                                  >
                                    <img src={editIcon} alt="Editar" />
                                    <p className={styles.editColleagueText}>
                                      EDITAR COLEGA
                                    </p>
                                  </button>
                                )}
                            </div>
                          </div>

                          {(() => {
                            if (
                              !evaluator ||
                              !(
                                evaluator.evaluatorTypeChoice === "Colleague" ||
                                evaluator.evaluatorTypeChoice === "Friend"
                              ) ||
                              evaluator.status !== "Pendente"
                            ) {
                              return null;
                            }

                            const now = new Date();
                            const end = new Date(activeEvaluation.endDate);
                            const daysLeft = Math.ceil(
                              (end.getTime() - now.getTime()) /
                                (1000 * 60 * 60 * 24)
                            );

                            if (daysLeft > 7 || daysLeft < 0) return null;

                            if (
                              hiddenEvaluatorWarnings[
                                `${evaluator.exployeeId}-${idx}`
                              ]
                            )
                              return null;

                            const sentDate = evaluator.sentDate
                              ? new Date(evaluator.sentDate)
                              : new Date(activeEvaluation.startDate);
                            const daysSince = Math.max(
                              0,
                              Math.floor(
                                (now.getTime() - sentDate.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              )
                            );

                            return (
                              <div className={styles.warningBox}>
                                <div className={styles.warningContent}>
                                  <img
                                    src={exclamationIcon}
                                    alt="Warning Icon"
                                    className={styles.warningIcon}
                                  />
                                  <div>
                                    <div className={styles.warningText}>
                                      {evaluator.evaluatorEmployeeName ||
                                        evaluator.clientName ||
                                        "Colega"}{" "}
                                      ainda não respondeu ({daysSince} dias
                                      desde o envio)
                                    </div>
                                    <div className={styles.warningSubText}>
                                      Alterar colega ou contactar pessoalmente.
                                    </div>
                                  </div>
                                </div>
                                <button
                                  className={styles.warningClose}
                                  onClick={() =>
                                    setHiddenEvaluatorWarnings((s) => ({
                                      ...s,
                                      [`${evaluator.exployeeId}-${idx}`]: true,
                                    }))
                                  }
                                >
                                  <img src={CloseIcon} alt="Fechar" />
                                </button>
                              </div>
                            );
                          })()}
                        </li>
                      ));
                    })()}
                  </ul>
                )}
            </div>
          ) : (
            user?.nextEvaluation && (
              <div className={styles.countdownSection}>
                {/* Conteúdo do countdown */}
                <div className={styles.countdownBox}>
                  <div className={styles.countdownSide}>
                    <img
                      src={manImg}
                      alt="Homem a segurar papel"
                      className={styles.countdownImage}
                    />
                  </div>
                  {activeEvaluation &&
                  activeEvaluation.statusReasonLabel === "Completed" ? (
                    <div className={styles.evaluationCompletedBox}>
                      <h1 className={styles.evaluationCompletedTitle}>
                        Avaliação {activeEvaluation.year}
                      </h1>
                      <div className={styles.evaluationCompletedButtons}>
                        <WhiteButton
                          text="COMO FUNCIONA"
                          onClick={handleOpenModal}
                          className={styles.evaluationBtn}
                          icon={InfoIcon}
                          size={14}
                          matchIconColorToText={true}
                        />
                      </div>
                      <p className={styles.evaluationCompletedText}>
                        Próxima avaliação:{" "}
                        {new Date(user.nextEvaluation).toLocaleDateString(
                          "pt-PT"
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className={styles.countdownCenter}>
                      <>
                        <h2 className={styles.countdownSubtitle}>
                          A tua próxima avaliação 360º está planeada para{" "}
                          {(() => {
                            const date = new Date(user.nextEvaluation);
                            const month = date.toLocaleString("pt-PT", {
                              month: "long",
                            });
                            const year = date.getFullYear();
                            return `${
                              month.charAt(0).toUpperCase() + month.slice(1)
                            } ${year}`;
                          })()}
                        </h2>
                        <div className={styles.countdownTimer}>
                          {(() => {
                            const evalDate = new Date(user.nextEvaluation);
                            const remaining: TimeRemaining =
                              getTimeUntil(evalDate);
                            if (remaining.isPast) return null;

                            const months = remaining.months;
                            const days = remaining.days;
                            const hours = remaining.hours;
                            const minutes = remaining.minutes;

                            return (
                              <>
                                {remaining.days >= 1 ? (
                                  <>
                                    <div>
                                      <div>{months}</div>
                                      <small>MESES</small>
                                    </div>
                                    <div>
                                      <div>{days}</div>
                                      <small>DIAS</small>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div>
                                      <div>{hours}</div>
                                      <small>HORAS</small>
                                    </div>
                                    <div>
                                      <div>{minutes}</div>
                                      <small>MINUTOS</small>
                                    </div>
                                  </>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        <WhiteButton
                          text="COMO FUNCIONA"
                          onClick={handleOpenModal}
                          className={styles.countdownBtn}
                          icon={InfoIcon}
                          size={14}
                          matchIconColorToText={true}
                        />
                      </>
                    </div>
                  )}
                  <div className={styles.countdownSide}>
                    <img
                      src={womanImg}
                      alt="Mulher a gesticular"
                      className={styles.countdownImage}
                    />
                  </div>
                </div>
              </div>
            )
          )}

          {/* Listas de avaliações */}
          {(homeDashboard?.myPendingEvaluations?.length ?? 0) > 0 && (
            <EvaluateColleaguesList
              data={homeDashboard?.myPendingEvaluations ?? []}
              loading={loadingDashboard}
            />
          )}

          {(user?.typeLabel === "Team Leader" ||
            user?.typeLabel === "Management" ||
            user?.typeLabel === "RH") && (
            <>
              {(user?.typeLabel === "Management" || user?.typeLabel === "RH") &&
              (teamLeadersList.length > 0 || internalsList.length > 0) ? (
                <div className={styles.sidePanel}>
                  {teamLeadersList.length > 0 && (
                    <EvaluateTeamList
                      data={teamLeadersList}
                      loading={loadingDashboard}
                      onStartEvaluation={() => navigate("/select-colleagues")}
                      onShowToast={(msg) => setGlobalToast(msg)}
                      title="AVALIAR A MINHA EQUIPA"
                      subtitle="Avalia e acompanha a tua equipa."
                    />
                  )}
                  {internalsList.length > 0 && (
                    <EvaluateTeamList
                      data={internalsList}
                      loading={loadingDashboard}
                      onStartEvaluation={() => navigate("/select-colleagues")}
                      onShowToast={(msg) => setGlobalToast(msg)}
                      title="OUTRAS AVALIAÇÕES A DECORRER/INICIAR"
                      subtitle="Colaboradores atribuídos à minha equipa"
                    />
                  )}
                </div>
              ) : (
                <EvaluateTeamList
                  data={teamLeaderOwnEvaluations}
                  loading={loadingDashboard}
                  onStartEvaluation={() => navigate("/select-colleagues")}
                  onShowToast={(msg) => setGlobalToast(msg)}
                />
              )}
            </>
          )}
        </>
      ) : (
        <div className={styles.loadingPlaceholder}></div>
      )}
    </div>
  );
}
